import React, { useMemo } from 'react';
import { computeUsersTab, initials, AVATAR_COLORS } from './overviewUsersCompute';

function PersonRow({ person, index, tone }) {
  const arrow = tone === 'up'
    ? `▲ ${Math.round(Math.abs(person.pct ?? 0))}%`
    : person.pct != null
      ? `▼ ${Math.round(Math.abs(person.pct))}%`
      : 'dormant';
  return (
    <div className="overview-person">
      <span
        className="overview-avatar"
        style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
      >
        {initials(person.name)}
      </span>
      <div className="grow">
        <b>{person.name}</b>
        <small>{person.role}{person.facility ? ` · ${person.facility}` : ''}</small>
      </div>
      <span className={`overview-delta ${tone === 'up' ? 'up' : 'down'}`}>{arrow}</span>
      {tone === 'down' && person.daysSinceActive != null && (
        <small className="overview-person-last">last {person.daysSinceActive}d</small>
      )}
    </div>
  );
}

function OverviewUsersTab({
  loginsCurrent,
  loginsPrior,
  messagesCurrent,
  referralsCurrent,
  facIds,
  asOfDay,
  loading,
}) {
  const stats = useMemo(
    () => computeUsersTab({
      loginsCurrent,
      loginsPrior,
      messagesCurrent,
      referralsCurrent,
      hubFacIds: facIds,
      asOfDay,
    }),
    [loginsCurrent, loginsPrior, messagesCurrent, referralsCurrent, facIds, asOfDay]
  );

  if (loading) return <div className="overview-loading">Loading users…</div>;

  return (
    <div className="overview-grid3">
      <div className="overview-box">
        <h3>Hub adoption</h3>
        <div className="overview-big">
          {stats.active.toLocaleString()} of {stats.users.toLocaleString()} active
        </div>
        <div className="overview-progress">
          <i style={{ width: `${Math.round(stats.activePct * 100)}%`, background: '#10b981' }} />
        </div>
        <div className="overview-kv-row">
          <span>Active (logged in &lt; 60d)</span>
          <b>{Math.round(stats.activePct * 100)}%</b>
        </div>
        <div className="overview-kv-row">
          <span>New users (no prior-window logins)</span>
          <b>{stats.newUsers.toLocaleString()}</b>
        </div>
        <div className="overview-kv-row">
          <span className={stats.dormant >= 3 ? 'warn' : ''}>Dormant 60+</span>
          <b className={stats.dormant >= 3 ? 'warn' : ''}>{stats.dormant.toLocaleString()}</b>
        </div>
        <div className="overview-kv-row">
          <span>MFA %</span>
          <b className="muted">—</b>
        </div>
        <div className="overview-kv-row">
          <span>Physicians vs staff (heuristic)</span>
          <b>{stats.physicians} / {stats.staff}</b>
        </div>
        <p className="overview-footnote">
          MFA and licensed seat totals are not in the API. Physician vs staff uses user_type text match.
        </p>
      </div>

      <div className="overview-box">
        <h3>
          Trending up
          <small className="overview-h3-sub">current window vs prior year window</small>
        </h3>
        {stats.trendingUp.length === 0 ? (
          <p className="overview-sub">No clear ups vs the prior period.</p>
        ) : (
          stats.trendingUp.map((p, i) => (
            <PersonRow key={p.userId} person={p} index={i} tone="up" />
          ))
        )}
      </div>

      <div className="overview-box">
        <h3>
          Trending down
          <small className="overview-h3-sub">login volume or dormant</small>
        </h3>
        {stats.trendingDown.length === 0 ? (
          <p className="overview-sub">No clear downs vs the prior period.</p>
        ) : (
          stats.trendingDown.map((p, i) => (
            <PersonRow key={p.userId} person={p} index={i} tone="down" />
          ))
        )}
      </div>
    </div>
  );
}

export default OverviewUsersTab;
